"use client"

import axios, { InternalAxiosRequestConfig } from "axios";
import { fetchAuthSession } from 'aws-amplify/auth';
import React, { ReactNode, useContext, useEffect } from 'react';
import { useIsC1Integrated } from '@/components/C1Integration/useIsC1Integrated';
import { GigyaContext, GigyaContextType } from '@/components/GigyaContext/GigyaLoader';
import { useIsLoggedIn } from "./useIsLoggedIn";

let axiosResolve: ((readiedAxios : typeof axios) => void);
const axiosPromise = new Promise<typeof axios>(resolve => axiosResolve = resolve);
export default axiosPromise;

// Has to be a component for next
// Sets up the axios interceptor, selecting the auth token depending on how we're being accessed
export const SetupAxiosInterceptor: React.FC<{ children?: ReactNode }> = ({children}) => {
  const isC1Integrated = useIsC1Integrated();
  const gigya = useContext(GigyaContext);
  const isLoggedIn = useIsLoggedIn();

  useEffect(() => {
    if(isC1Integrated === null || ! isLoggedIn) {
      return;
    }

    if(isC1Integrated) {
      if(gigya?.isGigyaLoaded) {
        axios.interceptors.request.use(createGigyaFulfilled(gigya), onError);
        axiosResolve(axios);
      }
    } else {
      axios.interceptors.request.use(createCognitoOnFulfilled(), onError);
      axiosResolve(axios);
    }
  }, [isC1Integrated, gigya, isLoggedIn]);

  return children;
}

const createGigyaFulfilled = (gigya: GigyaContextType) => async (config: InternalAxiosRequestConfig) => {
  const jwt = await gigya.fetchJwt();

  if(jwt) {
    config.headers.Authorization = `Bearer ${jwt}`;
  } else {
    console.error('Gigya authorization not found for backend request');
  }

  return config;
}

const createCognitoOnFulfilled = () => async (config: InternalAxiosRequestConfig) => {
  // Get the updated token from Cognito before each request
  const authSession = await fetchAuthSession();

  if (authSession?.tokens?.idToken) {
    const token = authSession?.tokens?.idToken.toString();

    // If the token exists, add it to the Authorization header
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }

  return config;
}

async function onError(error: unknown) {
  throw error;
}